import { useNavigate, useSearch } from '@tanstack/react-router';
import { useGT } from 'gt-react';

import Page from 'src/components/page/Page';
import { useLabelingCounts } from '@/api/rpc/hooks/ship-and-debit/useLabeling';
import type { LabelingMailboxFilter } from '@erp/rpc';
import { LabelingActions, LabelingQueueStats } from './LabelingHeader';
import { LabelingBrowseMode } from './LabelingBrowseMode';
import type { LabelingSearch } from './search';
import { useCanReview } from '../849-review/useCanReview';

const EMPTY_COUNTS = {
  unlabeledEmails: 0,
  reviewQueue: 0,
  labeledToday: 0,
};

/**
 * The 849 labeling page — the RECORD: every mailbox email with its label state
 * (Browse), beside the Labeled page.
 *
 * The review queue and claim aging moved to the review page, and the Matches mode
 * is gone (#7656). Nothing is answered here (#7657): every answer — a label
 * included — is given on the review page, which Browse opens any email in
 * ("Open in Review").
 */
export function LabelingPage() {
  const gt = useGT();
  const search = useSearch({ from: '/authenticated/ship-and-debit/labeling' });
  const navigate = useNavigate();
  const canReview = useCanReview();

  /** URL-backed state, so an open email survives a refresh and can be linked. */
  function setSearch(next: Partial<LabelingSearch>) {
    void navigate({
      to: '/ship-and-debit/labeling',
      search: (prev: LabelingSearch) => ({ ...prev, ...next }),
    });
  }
  const counts = useLabelingCounts();
  return (
    <Page
      title={gt('849 Labeling', { context: 'labeling page title' })}
      subtitle={gt(
        'Every email in the S&D mailbox and its label. Answer an email on the review page.',
        { context: 'labeling page subtitle' },
      )}
      actions={<LabelingActions canReview={canReview} />}
    >
      <div className="space-y-4">
        <LabelingQueueStats stats={counts.data ?? EMPTY_COUNTS} />

        <LabelingBrowseMode
          filter={search.filter ?? 'unlabeled'}
          onFilterChange={(f: LabelingMailboxFilter) =>
            setSearch({ filter: f })
          }
          search={search.q ?? ''}
          onSearchChange={(q) => setSearch({ q: q || undefined })}
          openEmailId={search.emailId ?? null}
          onOpenEmail={(id) => setSearch({ emailId: id ?? undefined })}
          canReview={canReview}
        />
      </div>
    </Page>
  );
}
